import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table';
import type { AdminItem, CaseItem, CaseSummary, ItemRarity } from '~/lib/contracts';

type AdminCasesPanelProps = {
    casesData: CaseSummary[];
    items: AdminItem[];
    caseItemsByCaseId: Record<number, CaseItem[]>;
    isBusy: boolean;
    onCreate: (payload: { name: string }) => Promise<void>;
    onUpdate: (caseId: number, payload: { name?: string }) => Promise<void>;
    onDelete: (caseId: number) => Promise<void>;
    onAssign: (caseId: number, itemId: number, rarity: ItemRarity) => Promise<void>;
    onUnassign: (caseId: number, itemId: number) => Promise<void>;
};

export function AdminCasesPanel({
    casesData,
    items,
    caseItemsByCaseId,
    isBusy,
    onCreate,
    onUpdate,
    onDelete,
    onAssign,
    onUnassign,
}: AdminCasesPanelProps) {
    const [newName, setNewName] = useState('');
    const [draftById, setDraftById] = useState<Record<number, { name: string }>>({});
    const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
    const [rarityByItemId, setRarityByItemId] = useState<Record<number, number>>({});
    const [selectedUnassignedItemId, setSelectedUnassignedItemId] = useState<number | null>(null);

    const sortedCases = useMemo(() => {
        return [...casesData].sort((a, b) => a.name.localeCompare(b.name));
    }, [casesData]);

    const sortedItems = useMemo(() => {
        return [...items].sort((a, b) => a.name.localeCompare(b.name));
    }, [items]);

    const activeCase = useMemo(() => {
        if (!selectedCaseId) {
            return null;
        }

        return sortedCases.find((caseData) => caseData.id === selectedCaseId) ?? null;
    }, [selectedCaseId, sortedCases]);

    const assignedByItemId = useMemo(() => {
        if (!selectedCaseId) {
            return new Map<number, CaseItem>();
        }

        const assignedItems = caseItemsByCaseId[selectedCaseId] ?? [];
        return new Map(assignedItems.map((item) => [item.id, item]));
    }, [caseItemsByCaseId, selectedCaseId]);

    const assignedItems = useMemo(() => {
        if (!selectedCaseId) {
            return [];
        }

        const nextAssignedItems = caseItemsByCaseId[selectedCaseId] ?? [];
        return [...nextAssignedItems].sort((a, b) => a.name.localeCompare(b.name));
    }, [caseItemsByCaseId, selectedCaseId]);

    const unassignedItems = useMemo(() => {
        return sortedItems.filter((item) => !assignedByItemId.has(item.id));
    }, [assignedByItemId, sortedItems]);

    useEffect(() => {
        if (!selectedCaseId) {
            setRarityByItemId({});
            return;
        }

        const assignedItems = caseItemsByCaseId[selectedCaseId] ?? [];
        const nextRarityByItemId: Record<number, number> = {};
        for (const item of assignedItems) {
            nextRarityByItemId[item.id] = item.rarity;
        }
        setRarityByItemId(nextRarityByItemId);
        setSelectedUnassignedItemId(null);
    }, [caseItemsByCaseId, selectedCaseId]);

    const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const name = newName.trim();
        if (!name) {
            return;
        }

        await onCreate({ name });
        setNewName('');
    };

    const handleDraftChange = (caseData: CaseSummary, value: string) => {
        setDraftById((current) => {
            const currentDraft = current[caseData.id] ?? {
                name: caseData.name,
            };

            return {
                ...current,
                [caseData.id]: {
                    ...currentDraft,
                    name: value,
                },
            };
        });
    };

    const getDraft = (caseData: CaseSummary) => {
        return draftById[caseData.id] ?? {
            name: caseData.name,
        };
    };

    const clampRarity = (rarity: number) => {
        return Math.max(0, Math.min(5, rarity)) as ItemRarity;
    };

    const getItemInitials = (name: string) => {
        const firstWord = name.trim().split(/\s+/).at(0);
        return firstWord?.slice(0, 2).toUpperCase() ?? 'IT';
    };

    const saveCaseNameOnBlur = (caseData: CaseSummary, nameValue: string) => {
        const nextName = nameValue.trim();
        if (!nextName || nextName === caseData.name) {
            return;
        }

        void onUpdate(caseData.id, { name: nextName });
    };

    const saveRarityOnBlur = (caseId: number, item: CaseItem, rarity: number) => {
        const nextRarity = clampRarity(rarity);
        if (nextRarity === item.rarity) {
            return;
        }

        void onAssign(caseId, item.id, nextRarity);
    };

    if (activeCase) {
        const activeDraft = getDraft(activeCase);

        return (
            <section className="rounded-xl border border-orange-500/25 bg-linear-to-br from-zinc-950 to-zinc-900 p-4 shadow-2xl">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="w-full max-w-xl">
                        <Input
                            value={activeDraft.name}
                            disabled={isBusy}
                            className="text-lg font-bold"
                            onChange={(event) => {
                                handleDraftChange(activeCase, event.currentTarget.value);
                            }}
                            onBlur={() => {
                                saveCaseNameOnBlur(activeCase, activeDraft.name);
                            }}
                        />
                        <p className="mt-1 text-sm text-white/60">Manage case item rarity values.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setSelectedCaseId(null);
                            }}
                        >
                            Back to cases
                        </Button>
                    </div>
                </div>

                <div className="mt-4 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <select
                            className="w-full max-w-sm rounded-lg border border-white/20 bg-black/50 px-2.5 py-2 text-white"
                            value={selectedUnassignedItemId ?? ''}
                            onChange={(event) => {
                                const nextId = Number.parseInt(event.currentTarget.value, 10);
                                if (!Number.isInteger(nextId)) {
                                    setSelectedUnassignedItemId(null);
                                    return;
                                }

                                setSelectedUnassignedItemId(nextId);
                            }}
                        >
                            <option value="">Select item to add</option>
                            {unassignedItems.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                        <Button
                            variant="secondary"
                            disabled={isBusy || !selectedUnassignedItemId}
                            onClick={() => {
                                if (!selectedUnassignedItemId) {
                                    return;
                                }

                                void onAssign(activeCase.id, selectedUnassignedItemId, 0);
                                setSelectedUnassignedItemId(null);
                            }}
                        >
                            Add item
                        </Button>
                    </div>
                    {assignedItems.map((item) => {
                        const currentRarity = rarityByItemId[item.id] ?? item.rarity;

                        return (
                            <article
                                key={item.id}
                                className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-xl border border-white/10 bg-white/2 p-3 md:grid-cols-[auto_minmax(0,1fr)_auto_auto]"
                            >
                                <Avatar size="lg">
                                    <AvatarImage src={item.imageUrl ?? undefined} alt={item.name} />
                                    <AvatarFallback>{getItemInitials(item.name)}</AvatarFallback>
                                </Avatar>
                                <p className="font-medium">{item.name}</p>
                                <Input
                                    type="number"
                                    min={0}
                                    max={5}
                                    className="w-24"
                                    value={currentRarity}
                                    onChange={(event) => {
                                        const nextValue = Number.parseInt(event.currentTarget.value, 10);
                                        if (!Number.isInteger(nextValue)) {
                                            return;
                                        }

                                        setRarityByItemId((current) => ({
                                            ...current,
                                            [item.id]: clampRarity(nextValue),
                                        }));
                                    }}
                                    onBlur={() => {
                                        saveRarityOnBlur(activeCase.id, item, currentRarity);
                                    }}
                                />
                                <div className="flex justify-end gap-2">
                                    <Button
                                        variant="destructive"
                                        disabled={isBusy}
                                        onClick={() => void onUnassign(activeCase.id, item.id)}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            </article>
                        );
                    })}
                    {assignedItems.length === 0 && (
                        <p className="text-sm text-white/60">No items assigned to this case yet.</p>
                    )}
                </div>
            </section>
        );
    }

    return (
        <section className="rounded-xl border border-orange-500/25 bg-linear-to-br from-zinc-950 to-zinc-900 p-4 shadow-2xl">
            <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold">Cases</h2>
                <span className="rounded-full border border-white/20 px-2.5 py-1 text-xs text-white/85">
                    {casesData.length}
                </span>
            </div>
            <form className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto]" onSubmit={(event) => void handleCreate(event)}>
                <Input
                    value={newName}
                    placeholder="Case name"
                    onChange={(event) => {
                        setNewName(event.currentTarget.value);
                    }}
                />
                <Button type="submit" disabled={isBusy}>
                    Add case
                </Button>
            </form>

            <div className="mt-4">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Items</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sortedCases.map((caseData) => {
                            const itemCount = caseItemsByCaseId[caseData.id]?.length ?? 0;

                            return (
                                <TableRow
                                    key={caseData.id}
                                    className="cursor-pointer"
                                    onClick={() => {
                                        setSelectedCaseId(caseData.id);
                                    }}
                                >
                                    <TableCell className="font-medium">{caseData.name}</TableCell>
                                    <TableCell>{itemCount}</TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                disabled={isBusy}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    void onDelete(caseData.id);
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {sortedCases.length === 0 && <p className="mt-3 text-sm text-white/60">No cases yet.</p>}
            </div>
        </section>
    );
}
